import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type SignUpResponse = {
  data?: {
    user: User | null;
    session: Session | null;
  };
  error: any;
  emailVerificationSent?: boolean;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<SignUpResponse>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ error: any }>;
  deleteUser: () => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const auth = useAuth();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("[AuthContext] Auth state changed:", event, session?.user?.id);
      
      if (event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        
        // Gérer la redirection après connexion
        const pendingRelationId = localStorage.getItem('pendingRelationId');
        const redirectPath = localStorage.getItem('redirectAfterAuth');
        const isNewUser = localStorage.getItem('isNewUser') === 'true';
        const isGoogleSignup = sessionStorage.getItem('google_signup') === 'true';
        const referralCode = localStorage.getItem('referral_code');
        
        console.log("[AuthContext] SIGNED_IN event with:", {
          pendingRelationId,
          redirectPath,
          isNewUser,
          isGoogleSignup,
          referralCode
        });
        
        // Traitement prioritaire pour les inscriptions Google
        if (isGoogleSignup && isNewUser) {
          console.log("[AuthContext] Google signup detected, immediately redirecting to signup flow");
          sessionStorage.removeItem('google_signup'); // Nettoyer le flag après utilisation
          
          // S'assurer que le code de parrainage est préservé
          if (referralCode) {
            console.log("[AuthContext] Preserving referral code for signup flow:", referralCode);
            // Déjà dans localStorage, rien à faire
          }
          
          window.location.replace(`${window.location.origin}/#/signup-flow`);
          return;
        }
        
        if (pendingRelationId) {
          console.log("[AuthContext] Found pending relation, preparing redirection");
          // La redirection sera gérée par PrivateRoute
          return;
        }
        
        if (redirectPath) {
          console.log("[AuthContext] Found redirect path:", redirectPath);
          // La redirection sera gérée par PrivateRoute
          return;
        }
        
        if (isNewUser) {
          console.log("[AuthContext] New user detected, redirecting to signup flow");
          // S'assurer que le code de parrainage est préservé
          if (referralCode) {
            console.log("[AuthContext] Preserving referral code for signup flow:", referralCode);
            // Déjà dans localStorage, rien à faire
          }
          
          // Rediriger immédiatement vers signup-flow pour les nouveaux utilisateurs
          window.location.replace(`${window.location.origin}/#/signup-flow`);
          return;
        }
        
        // Si pas de redirection spécifique, rediriger vers le profil
        if (window.location.pathname === '/login' || window.location.pathname === '/signup') {
          console.log("[AuthContext] Redirecting to profile after login");
          window.location.replace(`${window.location.origin}/#/profile`);
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        // Nettoyer les données de redirection
        localStorage.removeItem('redirectAfterAuth');
        localStorage.removeItem('pendingRelationId');
        localStorage.removeItem('redirectionInProgress');
        localStorage.removeItem('isNewUser');
        // Ne pas nettoyer referral_code pour conserver le contexte de parrainage
      } else if (event === 'USER_UPDATED') {
        setUser(session?.user ?? null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (auth.loading) {
    return (
      <div 
        className="min-h-screen bg-black flex items-center justify-center"
        role="status"
        aria-label="Chargement en cours"
      >
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-4 border-primary-end/20"></div>
          <div 
            className="absolute inset-0 rounded-full border-4 border-t-primary-end animate-spin"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      ...auth,
      user,
      loading: auth.loading
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}